import {
    AvenAdvisorBorrowingCapacityData,
    DepositBalanceAsOfDateData,
    HomeMetricsForDateRangeData,
    LiabilitiesData,
    MonthlySpendingComparsionData,
    NetWorthForDateRangeData,
    TopMerchantsByTotalAmountDescendingData,
    TopMerchantsDuration,
} from 'aven_types'
import { ref, shallowRef, Ref, ShallowRef } from 'vue'

// Types for personal financial statement resources available in frontend
export enum PersonalFinancialStatementResourceType {
    NET_WORTH_FOR_DATE_RANGE = 'netWorthForDateRange',
    DEPOSIT_BALANCE_AS_OF_DATE = 'depositBalanceAsOfDate',
    HOME_VALUE_AND_EQUITY_FOR_DATE_RANGE = 'homeValueAndEquityForDateRange',
    TOP_MERCHANTS = 'topMerchants',
    MONTHLY_SPENDING_COMPARISON = 'monthlySpendingComparison',
    LIABILITIES = 'liabilities',
    BORROWING_POWER = 'borrowingPower',
}

// Map between PersonalFinancialStatementResourceType and its data type
export type PersonalFinancialStatementResourceDataType = {
    [PersonalFinancialStatementResourceType.NET_WORTH_FOR_DATE_RANGE]: NetWorthForDateRangeData | null
    [PersonalFinancialStatementResourceType.DEPOSIT_BALANCE_AS_OF_DATE]: DepositBalanceAsOfDateData[]
    [PersonalFinancialStatementResourceType.HOME_VALUE_AND_EQUITY_FOR_DATE_RANGE]: HomeMetricsForDateRangeData[]
    [PersonalFinancialStatementResourceType.TOP_MERCHANTS]: Record<TopMerchantsDuration, TopMerchantsByTotalAmountDescendingData | null> | null
    [PersonalFinancialStatementResourceType.MONTHLY_SPENDING_COMPARISON]: MonthlySpendingComparsionData | null
    [PersonalFinancialStatementResourceType.LIABILITIES]: LiabilitiesData | null
    [PersonalFinancialStatementResourceType.BORROWING_POWER]: AvenAdvisorBorrowingCapacityData | null
}

export interface PersonalFinancialStatementFetchOptions {
    onlyFetchBetweenMostRecentSnapshotAndCurrentDate?: boolean
    isNewUserInitialization?: boolean
}

export abstract class BasePersonalFinancialStatementResource<T extends PersonalFinancialStatementResourceType> {
    private _data: ShallowRef<PersonalFinancialStatementResourceDataType[T] | null>
    private _fetchDataPromise: Promise<PersonalFinancialStatementResourceDataType[T] | null> | null = null
    private _hasData: Ref<boolean>
    private _isLoading: Ref<boolean>
    private _error: Ref<string | null>
    private _lastUpdated: Ref<Date | null>

    constructor(public readonly type: T) {
        this._data = shallowRef<PersonalFinancialStatementResourceDataType[T] | null>(null)
        this._hasData = ref<boolean>(false)
        this._isLoading = ref<boolean>(false)
        this._error = ref<string | null>(null)
        this._lastUpdated = ref<Date | null>(null)
    }

    protected abstract fetchData(options?: PersonalFinancialStatementFetchOptions): Promise<PersonalFinancialStatementResourceDataType[T] | null>

    public get data(): ShallowRef<PersonalFinancialStatementResourceDataType[T] | null> {
        return this._data
    }

    public get hasData(): Ref<boolean> {
        return this._hasData
    }

    public get isLoading(): Ref<boolean> {
        return this._isLoading
    }

    public get error(): Ref<string | null> {
        return this._error
    }

    public get lastUpdated(): Ref<Date | null> {
        return this._lastUpdated
    }

    public async maybeGetAndUpdateData(options?: PersonalFinancialStatementFetchOptions): Promise<PersonalFinancialStatementResourceDataType[T] | null> {
        // If already loading, return the existing promise to avoid duplicate requests
        if (this._fetchDataPromise) {
            return this._fetchDataPromise
        }

        this._isLoading.value = true
        this._error.value = null

        this._fetchDataPromise = this.fetchData(options)
            .then((result) => {
                this._data.value = result
                this._hasData.value = result !== null
                this._lastUpdated.value = new Date()
                this._error.value = null
                return result
            })
            .catch((error) => {
                this._error.value = error.message || `Failed to fetch data for resource type ${this.type}`
                this._hasData.value = false
                return null
            })
            .finally(() => {
                this._isLoading.value = false
                this._fetchDataPromise = null
            })

        return this._fetchDataPromise
    }
}
